<template>
  <v-card class="border">
    <v-card-title>{{ $ucfirst($t('journeyProgress.parameters')) }}</v-card-title>
    <v-simple-table class="patient-overview__table">
      <template v-slot:default>
        <tbody>
          <tr
            v-for="(item, index) in tableData"
            :key="`${item.label}-${index}`"
          >
            <td>
              <i :class="`v-icon patient-overview__icon ${item.icon}`"></i>
              {{ item.label }}
            </td>
            <td>
              <span
                v-html="`${item.value}${item.units}`"
                :class="item.class"
                v-if="item.value"
              ></span>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import {UCUMUnit} from "../../../../../helper/UCUMUnit";
import gql from 'graphql-tag';

/**
 * Component PatientOverviewParameters.vue
 *
 * @module PatientOverviewParameters
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.3] app/src/APP_DASHBOARD/templates/views/Patient/Overview/PatientOverviewParameters.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'PatientOverviewParameters',
  data() {
    return {
      parameterClass: {
        red: 'patient-overview-parameter--red',
        orange: 'patient-overview-parameter--orange',
        green: 'patient-overview-parameter--green',
      }
    };
  },
  computed: {
    ...mapGetters({
      bloodPressure: 'patientSummary/bloodPressure',
      weight: 'patientSummary/weight',
      length: 'patientSummary/length',
      cholesterolLDL: 'patientSummary/cholesterolLDL',
      cholesterolLDLInPreferredUnit: 'patientSummary/cholesterolLDLInPreferredUnit',
      cholesterolLDLPreferredUnitLabel: 'patientSummary/cholesterolLDLPreferredUnitLabel',
      glucose: 'patientSummary/glucose',
      glucoseInPreferredUnit: 'patientSummary/glucoseInPreferredUnit',
      glucosePreferredUnitLabel: 'patientSummary/glucosePreferredUnitLabel',
      patientId: 'patientSummary/id',
      hadEvent: 'patientSummary/hadSecondVascularEventOnStatins'
    }),
    tableData() {
      return [
        {
          label: this.$stripHTML(this.$ucfirst(this.$t('general.bloodPressure', { catalog : 'app_mobile'}))),
          value: this.bloodPressure,
          icon: 'icon-blood-pressure',
          units: ' ' + UCUMUnit.MILLIMETER_OF_MERCURY.translate(this.$t),
          class: this.bloodPressureClass,
        },
        {
          label: this.$stripHTML(this.$ucfirst(this.$t('parameters.weight', { catalog : 'content'}))),
          value: this.weight,
          icon: 'icon-weight',
          units: ' ' + UCUMUnit.KILOGRAM.translate(this.$t),
          class: this.BMIClass,
        },
        {
          label: this.$stripHTML(this.$ucfirst(this.$t('parameters.bmi'))),
          value: this.BMI,
          icon: 'icon-bmi',
          units: '  ' + UCUMUnit.KILOGRAM_PER_SQUARE_METER,
          class: this.BMIClass,
        },
        {
          label: this.$stripHTML(this.$ucfirst(this.$t('parameters.cholesterolldl'))),
          value: this.cholesterolLDLInPreferredUnit,
          icon: 'icon-cholesterol-ldl',
          units: ' ' + this.cholesterolLDLPreferredUnitLabel,
          class: this.cholesterolLDLClass,
        },
        {
          label: this.$stripHTML(this.$ucfirst(this.$t('general.hba1cGlucose', 'app_mobile'))),
          value: this.glucoseInPreferredUnit,
          icon: 'icon-glucose',
          units: ' ' + this.glucosePreferredUnitLabel,
          class: this.glucoseClass,
        },
      ];
    },
    bloodPressureClass() {
      const bloodPressure = parseInt(this.bloodPressure.split('/')[0]);

      if (bloodPressure > 160 || bloodPressure < 80) {
        return this.parameterClass.red;
      } else if (bloodPressure >= 130 || bloodPressure < 100) {
        return this.parameterClass.orange;
      } else {
        return this.parameterClass.green
      }
    },
    BMI() {
      const weight = parseFloat(this.weight);
      const length = this.length / 100;

      return Math.round((weight / (length * length)) * 10) / 10;
    },
    BMIClass() {
      if (this.BMI > 30 || this.BMI < 15) {
        return this.parameterClass.red;
      } else if (this.BMI > 20 && this.BMI <= 25) {
        return this.parameterClass.green;
      } else {
        return this.parameterClass.orange;
      }
    },
    cholesterolLDLClass() {
      const cholesterolLDL = parseFloat(this.cholesterolLDL);
      const minValue = this.hadEvent ? 1 : 1.4;
      const maxValue = this.hadEvent ? 1.4 : 1.8;

      // Convert from mg/dl to mmol/l - divide by 38.67
      const cholesterolLDLConverted = cholesterolLDL / 38.67;

      if (cholesterolLDLConverted < minValue) {
        return this.parameterClass.green;
      } else if (cholesterolLDLConverted > maxValue) {
        return this.parameterClass.red;
      } else {
        return this.parameterClass.orange;
      }
    },
    glucoseClass() {
      const glucose = parseFloat(this.glucose);

      if (glucose >= 6.5) {
        return this.parameterClass.red;
      } else if (glucose >= 6) {
        return this.parameterClass.orange;
      } else {
        return this.parameterClass.green;
      }
    },
  }
};
</script>

<style lang="scss">
.patient-overview-parameters__names {
  margin-right: 50px;
}
</style>
